import { useEffect, useState } from "react";

const cache = new Map<string, string>();
const clamp = (n: number, min: number, max: number) => Math.min(max, Math.max(min, n));

/**
 * Samples an artwork image and returns a dark, saturated version of its
 * average color, hex-encoded — used as the "colored wash" behind the
 * track/playlist hero, the way Spotify/Apple Music derive a background
 * tint from the current artwork.
 *
 * The artwork CDN (DigitalOcean Spaces) doesn't send CORS headers, so a
 * plain <img crossOrigin="anonymous"> fails to load and a direct canvas
 * read would taint anyway. We route the image through wsrv.nl — a
 * CORS-enabled image proxy/resizer — and ask for a tiny 16x16 copy, which
 * is both fast and enough to average. Falls back to `fallback` if the
 * image can't be read for any reason.
 */
export function useDominantColor(imageUrl?: string, fallback = "#460505") {
  const [color, setColor] = useState(
    () => (imageUrl && cache.get(imageUrl)) || fallback
  );

  useEffect(() => {
    if (!imageUrl) {
      setColor(fallback);
      return;
    }

    const cached = cache.get(imageUrl);
    if (cached) {
      setColor(cached);
      return;
    }

    let cancelled = false;
    const proxiedUrl = `https://wsrv.nl/?url=${encodeURIComponent(
      imageUrl.replace(/^https?:\/\//, "")
    )}&w=16&h=16&fit=cover`;

    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => {
      if (cancelled) return;
      try {
        const size = 16;
        const canvas = document.createElement("canvas");
        canvas.width = size;
        canvas.height = size;
        const ctx = canvas.getContext("2d");
        if (!ctx) throw new Error("no 2d context");
        ctx.drawImage(img, 0, 0, size, size);
        const { data } = ctx.getImageData(0, 0, size, size);

        let r = 0;
        let g = 0;
        let b = 0;
        let count = 0;
        for (let i = 0; i < data.length; i += 4) {
          if (data[i + 3] < 32) continue;
          r += data[i];
          g += data[i + 1];
          b += data[i + 2];
          count++;
        }
        if (!count) throw new Error("no visible pixels");
        r /= count;
        g /= count;
        b /= count;

        // Push toward a richer, darker tone so white text stays legible
        // regardless of how light/pale the artwork itself is.
        const [h, s] = rgbToHsl(r, g, b);
        const finalColor = hslToHex(h, clamp(s * 1.3, 0.35, 0.85), 0.22);

        cache.set(imageUrl, finalColor);
        if (!cancelled) setColor(finalColor);
      } catch {
        // Proxy failure / tainted canvas — keep the fallback color.
      }
    };
    img.onerror = () => {};
    img.src = proxiedUrl;

    return () => {
      cancelled = true;
    };
  }, [imageUrl, fallback]);

  return color;
}

function rgbToHsl(r: number, g: number, b: number): [number, number, number] {
  r /= 255;
  g /= 255;
  b /= 255;
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  let h = 0;
  let s = 0;
  const l = (max + min) / 2;
  const d = max - min;
  if (d !== 0) {
    s = d / (1 - Math.abs(2 * l - 1));
    switch (max) {
      case r:
        h = ((g - b) / d) % 6;
        break;
      case g:
        h = (b - r) / d + 2;
        break;
      default:
        h = (r - g) / d + 4;
    }
    h *= 60;
    if (h < 0) h += 360;
  }
  return [h, s, l];
}

function hslToHex(h: number, s: number, l: number): string {
  const c = (1 - Math.abs(2 * l - 1)) * s;
  const x = c * (1 - Math.abs(((h / 60) % 2) - 1));
  const m = l - c / 2;
  let [r, g, b] = [0, 0, 0];
  if (h < 60) [r, g, b] = [c, x, 0];
  else if (h < 120) [r, g, b] = [x, c, 0];
  else if (h < 180) [r, g, b] = [0, c, x];
  else if (h < 240) [r, g, b] = [0, x, c];
  else if (h < 300) [r, g, b] = [x, 0, c];
  else [r, g, b] = [c, 0, x];

  const toHex = (v: number) => {
    const n = Math.round((v + m) * 255);
    return clamp(n, 0, 255).toString(16).padStart(2, "0");
  };
  return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
}
